"use client";

import type { SurveyQuestion } from "@/types/survey";
import type { PUBLIC_SURVEY_UI_COPY } from "@/lib/surveyLanguages";
import { Button } from "@/design-system";
import { getMomentusOptionsConfig } from "@/lib/momentusOptions";
import { useMomentusOptionsPreview } from "@/hooks/useMomentusOptionsPreview";
import { isSelectOtherEnabled } from "@/lib/selectOtherOption";
import {
  ContentImageBlock,
  ContentRichtextBlock,
  ContentTextBlock,
  ContentVideoBlock,
  CountryQuestion,
  FileUploadQuestion,
  MatrixQuestion,
  RankingQuestion,
  MultiSelectQuestion,
  NPSRatingQuestion,
  RadioBooleanQuestion,
  RatingQuestion,
  RatingWordQuestion,
  SingleSelectQuestion,
  TextQuestion,
} from "@/components/survey-questions";
import { normalizeQuestionType } from "./questionRegistry";

export type AnswerValue = string | number | boolean | string[];
export type SurveyRendererUiStrings = (typeof PUBLIC_SURVEY_UI_COPY)["en"];

interface SurveyRendererBaseProps {
  question: SurveyQuestion;
  uiStrings?: SurveyRendererUiStrings;
  /** Brand color forwarded to widgets that paint themselves with it
   *  (rating, NPS, rating-word, selects, matrix, file-upload). */
  brandColor: string;
  previewMomentusOptions?: boolean;
  onRetryMomentusOptions?: (questionId: string) => void;
  momentusOptionsRetrying?: boolean;
}

interface SurveyRendererPreviewProps extends SurveyRendererBaseProps {
  mode: "preview";
}

interface SurveyRendererLiveProps extends SurveyRendererBaseProps {
  mode: "live";
  /** Current answer value for this question, indexed by `question.id`
   *  in the parent state. Used by widgets that need to reflect the
   *  stored value (text, textarea, matrix, file-upload). Selection state
   *  for choice-style widgets goes through `isSelected`. */
  answer: AnswerValue | undefined;
  isSelected: (
    question: SurveyQuestion,
    candidate: string | number,
  ) => boolean;
  onAnswerChange: (question: SurveyQuestion, value: string | string[]) => void;
  /** Optional survey id required for real file uploads. The
   *  FileUploadQuestion widget falls back to a no-network preview when
   *  this is omitted. */
  surveyId?: string;
}

export type SurveyRendererProps =
  | SurveyRendererPreviewProps
  | SurveyRendererLiveProps;

/** Single source of truth for mapping a question type to its renderer.
 *
 *  Both the edit builder preview and the public respondent
 *  page consume this component, so adding or renaming a question type
 *  only touches `questionRegistry.ts` and this switch — never a route
 *  or editor file. */
export function SurveyRenderer(props: SurveyRendererProps) {
  if (getMomentusOptionsConfig(props.question)?.enabled || props.question.momentusOptions) {
    return <MomentusSelectRenderer {...props} />;
  }
  return <StaticSurveyRenderer {...props} />;
}

function MomentusSelectRenderer(props: SurveyRendererProps) {
  const preview = props.mode === "preview" || props.previewMomentusOptions === true;
  const query = useMomentusOptionsPreview(props.question, preview);
  const snapshot = preview ? query.data : props.question.momentusOptions;
  const loading = snapshot?.status === "loading" || (preview && query.isFetching && !snapshot);
  const failed = snapshot?.status === "error" || (preview && query.isError);
  const choices = snapshot?.choices || [];
  const allowOther = snapshot?.allowOther ?? isSelectOtherEnabled(props.question);
  const question: SurveyQuestion = {
    ...props.question,
    choices: choices.map((choice) => ({ ...choice, isDefault: false, createdAt: "", updatedAt: "" })),
    options: { ...props.question.options, allowOther },
    required: loading || choices.length || allowOther ? props.question.required : false,
  };
  const retry = preview ? () => { void query.refetch(); } : props.onRetryMomentusOptions ? () => props.onRetryMomentusOptions!(question.id) : undefined;
  return (
    <>
      {loading ? <p role="status">Loading events…</p> : failed ? (
        <div role="status">
          <p>We couldn’t load events.</p>
          {retry ? <Button size="sm" variant="secondary" onClick={retry} disabled={query.isFetching || props.momentusOptionsRetrying} data-activity-label="Retry Momentus event options">Retry</Button> : null}
        </div>
      ) : snapshot && choices.length === 0 ? <p role="status">No events found for this period.</p> : null}
      <StaticSurveyRenderer {...props} question={question} />
    </>
  );
}

function StaticSurveyRenderer(props: SurveyRendererProps) {
  const { question, brandColor, uiStrings } = props;
  const type = normalizeQuestionType(question.questionType);

  if (type === null) {
    return null;
  }

  switch (type) {
    case "content-text":
      return <ContentTextBlock question={question} />;
    case "content-richtext":
      return <ContentRichtextBlock question={question} />;
    case "content-image":
      return <ContentImageBlock question={question} />;
    case "content-video":
      return <ContentVideoBlock question={question} />;
    case "rating":
    case "csat":
      return props.mode === "preview" ? (
        <RatingQuestion
          question={question}
          brandColor={brandColor}
          isPreview
          uiStrings={uiStrings}
        />
      ) : (
        <RatingQuestion
          question={question}
          brandColor={brandColor}
          isSelected={props.isSelected}
          onAnswerChange={props.onAnswerChange}
          uiStrings={uiStrings}
        />
      );
    case "rating-nps":
      return props.mode === "preview" ? (
        <NPSRatingQuestion
          question={question}
          brandColor={brandColor}
          isPreview
          uiStrings={uiStrings}
        />
      ) : (
        <NPSRatingQuestion
          question={question}
          brandColor={brandColor}
          isSelected={props.isSelected}
          onAnswerChange={props.onAnswerChange}
          uiStrings={uiStrings}
        />
      );
    case "rating-word":
      return props.mode === "preview" ? (
        <RatingWordQuestion
          question={question}
          brandColor={brandColor}
          isPreview
          uiStrings={uiStrings}
        />
      ) : (
        <RatingWordQuestion
          question={question}
          brandColor={brandColor}
          isSelected={props.isSelected}
          onAnswerChange={props.onAnswerChange}
          uiStrings={uiStrings}
        />
      );
    case "radio":
    case "boolean":
      return props.mode === "preview" ? (
        <RadioBooleanQuestion
          question={question}
          brandColor={brandColor}
          isPreview
          uiStrings={uiStrings}
        />
      ) : (
        <RadioBooleanQuestion
          question={question}
          isSelected={props.isSelected}
          onAnswerChange={props.onAnswerChange}
          uiStrings={uiStrings}
        />
      );
    case "text":
    case "textarea":
      return props.mode === "preview" ? (
        <TextQuestion
          question={question}
          brandColor={brandColor}
          isPreview
          uiStrings={uiStrings}
        />
      ) : (
        <TextQuestion
          question={question}
          answer={typeof props.answer === "string" ? props.answer : ""}
          onAnswerChange={props.onAnswerChange}
          uiStrings={uiStrings}
        />
      );
    case "single-select":
      return props.mode === "preview" ? (
        <SingleSelectQuestion
          question={question}
          brandColor={brandColor}
          isPreview
          uiStrings={uiStrings}
        />
      ) : (
        <SingleSelectQuestion
          question={question}
          brandColor={brandColor}
          isSelected={props.isSelected}
          onAnswerChange={props.onAnswerChange}
          selectedValue={
            typeof props.answer === "string" || typeof props.answer === "number"
              ? props.answer
              : undefined
          }
          uiStrings={uiStrings}
        />
      );
    case "multi-select":
      return props.mode === "preview" ? (
        <MultiSelectQuestion
          question={question}
          brandColor={brandColor}
          isPreview
          uiStrings={uiStrings}
        />
      ) : (
        <MultiSelectQuestion
          question={question}
          brandColor={brandColor}
          isSelected={props.isSelected}
          onAnswerChange={props.onAnswerChange}
          selectedValue={Array.isArray(props.answer) ? props.answer : undefined}
          uiStrings={uiStrings}
        />
      );
    case "country":
      return props.mode === "preview" ? (
        <CountryQuestion
          question={question}
          brandColor={brandColor}
          isPreview
          uiStrings={uiStrings}
        />
      ) : (
        <CountryQuestion
          question={question}
          brandColor={brandColor}
          isSelected={props.isSelected}
          onAnswerChange={props.onAnswerChange}
          uiStrings={uiStrings}
        />
      );
    case "matrix":
      return props.mode === "preview" ? (
        <MatrixQuestion
          question={question}
          brandColor={brandColor}
          isPreview
          uiStrings={uiStrings}
        />
      ) : (
        <MatrixQuestion
          question={question}
          brandColor={brandColor}
          answerValue={typeof props.answer === "string" ? props.answer : ""}
          onAnswerChange={props.onAnswerChange}
          uiStrings={uiStrings}
        />
      );
    case "ranking":
      return props.mode === "preview" ? (
        <RankingQuestion
          question={question}
          brandColor={brandColor}
          isPreview
          uiStrings={uiStrings}
        />
      ) : (
        <RankingQuestion
          question={question}
          brandColor={brandColor}
          answerValue={typeof props.answer === "string" ? props.answer : ""}
          onAnswerChange={props.onAnswerChange}
          uiStrings={uiStrings}
        />
      );
    case "file-upload":
      return props.mode === "preview" ? (
        <FileUploadQuestion
          question={question}
          brandColor={brandColor}
          isPreview
          uiStrings={uiStrings}
        />
      ) : (
        <FileUploadQuestion
          question={question}
          brandColor={brandColor}
          onAnswerChange={props.onAnswerChange}
          answer={props.answer}
          surveyId={props.surveyId}
          uiStrings={uiStrings}
        />
      );
  }
}
